import { faEllipsisV, faEye, faPencilAlt, faTimes } from "@fortawesome/free-solid-svg-icons";
import { faCopy, faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FindestEditableSpan, LetteredUserCircle, ListMenu, TitledIconChild } from "Components";
import React, { FC, useContext, useEffect, useState } from "react";
import { TSavedQueryTempDTO } from "Types";

import { SavedQueryControllerSingleton } from "Controllers";
import toast from "react-hot-toast";

// styles
import styles from "./queryTab.module.scss";
import { SavedQueryContext } from "Providers";
import { TechnologyConstants } from "Constants";

type TQueryTabProps = {
    selectedQueryGuid?: string,
    query: TSavedQueryTempDTO,
    selectedTechnologyId: number,
    caseId: number,
    clickCloseQuery: (currentQuery: TSavedQueryTempDTO) => void,
    clickOnQuery: (currentQuery: TSavedQueryTempDTO) => void,
    addToCaseQueryCount?: (count: number) => void,
    addToTechnologyQueryCount?: (technologyId: number, count: number) => void,
    onDeleteQuery?: (queryId: string) => void,
    onRenameQuery?: (queryId: string, name: string) => void
};

export const QueryTab: FC<TQueryTabProps> = ({selectedQueryGuid, query, selectedTechnologyId, caseId, clickCloseQuery, 
        clickOnQuery, addToCaseQueryCount, addToTechnologyQueryCount, onDeleteQuery, onRenameQuery}: TQueryTabProps) => {

    // Context
    const { updateSavedQuery } = useContext(SavedQueryContext);

    // State
    const [queryName, setQueryName] = useState(query.name);
    const [isEllipseMenuOpen, setIsEllipseMenuOpen] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [isFollowed] = useState(query.isMonitoredByCurrentUser);

    const closeAllMenus = () => {
        setIsEllipseMenuOpen(false);
        setIsEditingName(false);
    };

    useEffect(() => {
        if(selectedQueryGuid && selectedQueryGuid !== query.guid) {
            closeAllMenus();
        }
    }, [selectedQueryGuid, query]);

    const closeEllipseMenu = () => {
        setIsEllipseMenuOpen(false);
    };

    // const onEllipseMenuFollowClick = () => {
    //     setIsFollowed(!isFollowed);
    //     closeEllipseMenu();
    // }

    const onDuplicateQueryClick = async () => {
        const newQuery = await SavedQueryControllerSingleton.saveFirstDeliveryQueryChange(query,
            selectedTechnologyId === TechnologyConstants.GENERAL_TECHNOLOGY_ID ? "case" : "technology",
            selectedTechnologyId === TechnologyConstants.GENERAL_TECHNOLOGY_ID ? caseId : selectedTechnologyId);

        updateSavedQuery(newQuery);
        if(selectedTechnologyId === TechnologyConstants.GENERAL_TECHNOLOGY_ID) {
            if(addToCaseQueryCount) addToCaseQueryCount(1);
        }
    };

    const onEllipseDuplicateItemClick = () => {
        onDuplicateQueryClick();
        closeEllipseMenu();
    };

    const onEllipseDeleteItemClick = async () => {
        const isSuccess = await SavedQueryControllerSingleton.delete(query);
        if(isSuccess) {

            clickCloseQuery(query);
            closeEllipseMenu();

            if(selectedTechnologyId === TechnologyConstants.GENERAL_TECHNOLOGY_ID) {
                if(addToCaseQueryCount) addToCaseQueryCount(-1);
            } else {
                if(addToTechnologyQueryCount) addToTechnologyQueryCount(selectedTechnologyId, -1);
            }

            if(onDeleteQuery) onDeleteQuery(query.guid);
        } else {
            toast.error("Could not delete query.");
        }
    };

    const onRenamedQuery = async (newName: string) => {
        const isSuccess = await SavedQueryControllerSingleton.rename(query, newName);
        if(!isSuccess) {
            toast.error("Could not rename query.");
            return;
        }
        
        setQueryName(newName);
        if(onRenameQuery) onRenameQuery(query.guid, newName);
    };

    const onRenameStatusChange = (editStatus: boolean) => {
        if(editStatus !== isEditingName) {
            setIsEditingName(editStatus);
        }
    };

    const onRenameEnter = (_text: string, _inputRef: HTMLInputElement) => {
        setIsEditingName(false);
    };

    const onEllipseMenuRenameClick = () => {
        closeEllipseMenu();
        setIsEditingName(!isEditingName);
    };

    const onEllipseCloseItemClick = () => {
        closeEllipseMenu();
        clickCloseQuery(query);
    };

    const onRightClick: React.MouseEventHandler<HTMLDivElement> = (event: React.MouseEvent) => {
        clickOnQuery(query);
        setIsEllipseMenuOpen(true);
        event.preventDefault();
    };

    return (
        <div className={styles.queryTabContainer}>
            <div onContextMenu={onRightClick} onClick={() => { clickOnQuery(query); } } className={`${styles.queryTab} ${selectedQueryGuid && selectedQueryGuid === query.guid ? styles.selected : ""}`}>
                {!query.isUserAdded && 
                    <LetteredUserCircle username={query.username} className={styles.queryTabUserCircle}/>
                }
                {isFollowed && 
                    <span className={styles.monitoringEye}>
                        <FontAwesomeIcon icon={faEye} />
                    </span>
                }
                <FindestEditableSpan text={query.name} title={query.name} entityId={query.guid} onChangeText={onRenamedQuery} forceIsEditing={isEditingName} 
                    onEditStatusChange={onRenameStatusChange} doNotDisplayEditPencil={true} isEditOnDoubleClickDisabled={!query.isOwnedByCurrentUser} 
                    isQueryTabTitleClassName={styles.tabTitle} isQueryTabChangeTitleClassName={styles.changeTabTitle} doIgnoreTextUpdates={true} isQueryTab={true}
                    doAutoFocus={true} onEnter={onRenameEnter} />
                <span className={styles.queryTabOptions} onClick={() => { setIsEllipseMenuOpen(!isEllipseMenuOpen); }} title="Query options"><FontAwesomeIcon icon={faEllipsisV} /></span>
                <span className={styles.queryTabClose} onClick={(event: React.MouseEvent) => { event.stopPropagation(); clickCloseQuery(query); }} title="Close query"><FontAwesomeIcon icon={faTimes} /></span>
            </div>
            {isEllipseMenuOpen ?
                <ListMenu onClickOutsideCallback={closeAllMenus}>
                    {/* <TitledIconChild title={isFollowed ? "Unfollow" : "Follow"} icon={isFollowed ? faEyeSlash : faEye} onClick={ onEllipseMenuFollowClick } /> */}
                    {query.isOwnedByCurrentUser ?
                        <TitledIconChild title={isEditingName ? "Stop Renaming" : "Rename"} icon={faPencilAlt} onClick={ onEllipseMenuRenameClick } />
                        : null
                    }
                    <TitledIconChild title={"Duplicate"} icon={faCopy} onClick={ onEllipseDuplicateItemClick } />
                    {query.isOwnedByCurrentUser ?
                        <TitledIconChild title={"Delete"} icon={faTrashAlt} onClick={ onEllipseDeleteItemClick } />
                        : null
                    }
                    <TitledIconChild title={"Close"} icon={faTimes} onClick={ onEllipseCloseItemClick } />
                </ListMenu>
                : null }
        </div>
    );
};